import * as React from 'react';
import * as classnames from 'classnames';
import { withRouter, RouteComponentProps } from 'react-router';
import { PROJECT_SELECTION, LAUNCH_PROJECT } from '../../../constants/routes';
import { interpolate } from '../../../utils/string';
import NotifyHOC from '../../../HOC/Notifications';
import {
    VECTOR_MAP_REGION_CODES,
    VMAP_OVALUE,
    FILTER_VECTOR_MAP_CODES,
    PACKAGED_SOLUTION_QIDS
} from '@nexxt/common/constants';
import { Region, QTYPES, OptionData } from '@nexxt/common/types';
import { VectorMap } from '@nexxt-intelligence/react-jvectormap';
import CheckboxMultiSelect from '../../common/reactSelect/CheckboxMultiSelect';
import {
    UilImagePlus,
    UilDesktop,
    UilMobileAndroid,
    UilTablet,
    UilTimes,
    UilCheck,
    UilSync
} from '@iconscout/react-unicons';
import { last } from '@nexxt/common/utils/index';
import MediaLibraryAddModal from '../../libraries/mediaLibrary/MediaLibraryAddModal';
import { devices, deviceMapping } from '../../../mapping/overview';
import { SETUP_WIZARD_FORM_DATA_KEYS } from '../../../interface/project.interface';
import {
    VIDEO_TEMPLATE,
    COPY_TEMPLATE,
    IMAGE_TEMPLATE
} from '../../../constants/projectTemplate';
import * as projectService from '../../../services/project.service';
import Spinner from '../../common/Loader/Spinner';
import { alertMessage } from '../../../constants/alertMessage';
import ComputerVision from '../questions/common/ComputerVision';
import MediaManager from '../../libraries/mediaLibrary/MediaManager';

interface SetupWizardProps {
    toastNotification: any;
}

interface Country {
    code: string;
    value: string;
    label: string;
}

interface FormData {
    name: string;
    country: Country[];
    sampleSize: number;
    supportedDevices: string[];
    video: string;
    copyText: string;
    hotspot: string;
    hotspotOptions: any[];
}

const defaultFormData: FormData = {
    name: '',
    country: [],
    sampleSize: null,
    supportedDevices: [],
    video: null,
    copyText: '',
    hotspot: null,
    hotspotOptions: []
};

const WORD_NUM_BREAKPOINTS = [40, 100]; // breakpoints for sm and md word-chunk font-size

const SetupWizard: React.FC = (
    props: RouteComponentProps & SetupWizardProps
) => {
    const getTemplate = () => {
        switch (props.history.location.state?.solution?.projectID) {
            case PACKAGED_SOLUTION_QIDS.RAPID_VIDEO_AD:
                return VIDEO_TEMPLATE;
            case PACKAGED_SOLUTION_QIDS.RAPID_COPY_TEST:
                return COPY_TEMPLATE;
            case PACKAGED_SOLUTION_QIDS.RAPID_IMAGE_TEST:
                return IMAGE_TEMPLATE;
            default:
                return;
        }
    };
    const updatedDefaultFormData = () => {
        const newFormData = { ...defaultFormData };
        if (template.recommendedScreens) {
            newFormData.supportedDevices = template.recommendedScreens.map(
                (screen) => screen.label
            );
        }

        return newFormData;
    };
    const [template, setTemplate] = React.useState(getTemplate());
    const [step, setStep] = React.useState(0);
    const [formData, setFormData] = React.useState(updatedDefaultFormData());
    const [showCustomInput, setShowCustomInput] = React.useState(false);
    const [questionError, setQuestionError] = React.useState(null);
    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = React.useState(false);
    const [isRequesting, setIsRequesting] = React.useState(false);
    const [enableComputerVision, setEnableComputerVision] = React.useState(
        props.history.location.state?.solution?.projectID ===
            PACKAGED_SOLUTION_QIDS.RAPID_IMAGE_TEST
    );
    const [isLoading, setIsLoading] = React.useState(false);

    const toggleMediaLibrary = () => {
        setIsMediaLibraryOpen((prevState) => !prevState);
    };

    const selectMedia = (selectedMedia: any, updatedField: string) => {
        updateFormData(updatedField, selectedMedia.src);
        isMediaLibraryOpen && toggleMediaLibrary();
    };

    const validateCurrentValue = (step: number): string => {
        const type = template.questions[step].type;
        let error;

        switch (type) {
            case SETUP_WIZARD_FORM_DATA_KEYS.NAME:
                if (formData.name.trim() === '') {
                    error = 'Project name must not be empty';
                }
                break;
            case SETUP_WIZARD_FORM_DATA_KEYS.COUNTRY:
                if (formData.country.length < 1) {
                    error = 'You must select at least one country';
                }
                break;
            case SETUP_WIZARD_FORM_DATA_KEYS.SAMPLE_SIZE:
                if (formData.sampleSize <= 0) {
                    error = 'Number of completes must be great than 0';
                }
                break;
            case SETUP_WIZARD_FORM_DATA_KEYS.VIDEO:
                if (!formData.video) {
                    error = 'Please upload a video';
                }
                break;
            case SETUP_WIZARD_FORM_DATA_KEYS.SUPPORTED_DEVICES:
                if (formData.supportedDevices.length < 1) {
                    error = 'You must select at least one device type';
                }
                break;
            case SETUP_WIZARD_FORM_DATA_KEYS.COPY_TEXT:
                if (formData.copyText.trim() === '') {
                    error = 'Copy text must not be empty';
                }
                break;
            case SETUP_WIZARD_FORM_DATA_KEYS.HOTSPOT:
                if (!formData.hotspot) {
                    error = 'Please upload an image';
                }
                if (formData.hotspotOptions.length < 1) {
                    error = 'You must add at least 1 hotspot area';
                }
                break;
        }

        return error;
    };

    const updateFormData = (type: string, value: any) => {
        setFormData((prevState) => ({ ...prevState, [type]: value }));
    };

    const handleStep = (step: number, next: number) => {
        const error = validateCurrentValue(step);

        if (!error || next < step) {
            setShowCustomInput(false);
            setQuestionError(null);
            setStep(next);
        } else {
            setQuestionError(error);
        }
    };

    const handleLaunch = async () => {
        const notComplete = Array.from(Array(template.questions.length)).filter(
            (q, i) => {
                const error = validateCurrentValue(i);
                return error;
            }
        );
        if (notComplete.length > 0) {
            return setQuestionError(
                'Please complete all questions to launch project'
            );
        }

        const formattedSupportedDevices = formData.supportedDevices.map(
            (deviceLabel) => {
                const device = devices.find((d) => d.label === deviceLabel);
                return device.value;
            }
        );
        const formattedCountries = formData.country.map((co) => ({
            label: co.label,
            value: co.code
        }));
        const templateSettings = props.location.state.solution;
        const formatFormData = {
            ...formData,
            supportedDevices: formattedSupportedDevices,
            country: formattedCountries
        };

        try {
            setIsRequesting(true);
            const response = await projectService.createSetupWizardProject(
                formatFormData,
                {
                    tenantID: templateSettings.tenantID,
                    projectID: templateSettings.projectID
                }
            );

            // ROUTE TO LAUNCH PAGE
            const solution = props.location.state.solution;
            props.history.push({
                pathname: `${interpolate(LAUNCH_PROJECT, {
                    projectId: response.data.projectId
                })}`,
                state: { solution }
            });
            setIsRequesting(false);
        } catch (error) {
            props.toastNotification.error(alertMessage.GENERIC_ERROR_MESSAGE);
            setIsRequesting(false);
        }
    };

    const getInput = (type: string) => {
        switch (type) {
            case SETUP_WIZARD_FORM_DATA_KEYS.NAME:
                return (
                    <>
                        <div className="setup-wizard--body--input">
                            <input
                                autoFocus
                                type="text"
                                placeholder="Name"
                                value={formData.name}
                                onChange={(e) =>
                                    updateFormData(
                                        SETUP_WIZARD_FORM_DATA_KEYS.NAME,
                                        e.target.value
                                    )
                                }
                            />
                        </div>
                        <div className="setup-wizard--important">
                            <span className="setup-wizard--important--content">
                                This is for your own reference. It will not be
                                visible to your participants.
                            </span>
                        </div>
                    </>
                );
            case SETUP_WIZARD_FORM_DATA_KEYS.COUNTRY:
                const WORLD_MAP_OBJECT = {
                    title: 'World Map (Countries)',
                    label: 'World Map (Countries)',
                    id: 'world_mill',
                    value: 'world_mill',
                    identifier: 'W'
                };

                const optionsObj = FILTER_VECTOR_MAP_CODES(
                    WORLD_MAP_OBJECT as Region
                );
                const options = Object.keys(optionsObj).map((key) => ({
                    value: key,
                    label: optionsObj[key]
                }));
                const sortedOptions = options.sort((a, b) =>
                    a.label.localeCompare(b.label)
                );

                const onSelect = (regions: Country[]) => {
                    const filteredRegions = regions.filter(
                        (r) => !formData.country.includes(r)
                    );
                    if (filteredRegions.length > 0) {
                        const newRegion = filteredRegions[0];
                        const code = last(newRegion.value.split('/'));
                        updateMapArray(code, newRegion.value, newRegion.label);
                    } else {
                        updateFormData(
                            SETUP_WIZARD_FORM_DATA_KEYS.COUNTRY,
                            regions
                        );
                    }
                };

                const onMapClick = (e, code: string) => {
                    const formattedCode = `${VMAP_OVALUE}${WORLD_MAP_OBJECT.identifier}/${code}`;
                    const label = VECTOR_MAP_REGION_CODES[formattedCode];

                    updateMapArray(code, formattedCode, label);
                };

                const updateMapArray = (
                    code: string,
                    value: string,
                    label: string
                ) => {
                    const selectedRegions = [...formData.country];
                    const existingRegionIndex = selectedRegions.findIndex(
                        (region) => region.code === code
                    );
                    const newRegion = { value, label, code };

                    if (existingRegionIndex > -1) {
                        selectedRegions.splice(existingRegionIndex, 1);
                    } else {
                        selectedRegions.push(newRegion);
                    }
                    updateFormData(
                        SETUP_WIZARD_FORM_DATA_KEYS.COUNTRY,
                        selectedRegions
                    );
                };

                const getSelectedMapRegions = () => {
                    const selectedRegions = formData.country.map((region) => {
                        return region.code;
                    });
                    return selectedRegions;
                };

                return (
                    <div className="setup-wizard--body--country">
                        <div className="setup-wizard--body--country--list form-group">
                            <CheckboxMultiSelect
                                options={sortedOptions}
                                value={formData.country}
                                onChangeCallback={(response) => {
                                    onSelect(response);
                                }}
                            />
                        </div>
                        <div className="setup-wizard--body--country--map vector-map-ui">
                            <VectorMap
                                key={'world_mill'}
                                map={'world_mill'}
                                backgroundColor="rgb(117, 181, 226)"
                                zoomOnScroll={false}
                                zoomButtons={true}
                                containerClassName="map ripple"
                                onRegionClick={onMapClick}
                                selectedRegions={getSelectedMapRegions()}
                                regionStyle={{
                                    selected: {
                                        fill: '#349a71'
                                    }
                                }}
                            />
                        </div>
                    </div>
                );
            case SETUP_WIZARD_FORM_DATA_KEYS.SAMPLE_SIZE:
                const choices = [25, 50, 100, 250, 500];

                if (
                    !showCustomInput &&
                    formData.sampleSize &&
                    !choices.includes(formData.sampleSize)
                ) {
                    setShowCustomInput(true);
                }

                const buttons = choices.map((choice, i) => (
                    <div key={i} className="setup-wizard--body--choices--item">
                        <button
                            onClick={() => {
                                updateFormData(
                                    SETUP_WIZARD_FORM_DATA_KEYS.SAMPLE_SIZE,
                                    choice
                                );
                                if (showCustomInput) {
                                    setShowCustomInput(false);
                                }
                            }}
                            className={`btn btn--full ${
                                formData.sampleSize === choice
                                    ? 'btn--blue'
                                    : 'btn--yellow'
                            }`}
                        >
                            {choice}
                        </button>
                    </div>
                ));

                return (
                    <>
                        <div className="setup-wizard--body--choices">
                            {buttons}
                            <div className="setup-wizard--body--choices--item">
                                <button
                                    onClick={() => {
                                        updateFormData(
                                            SETUP_WIZARD_FORM_DATA_KEYS.SAMPLE_SIZE,
                                            null
                                        );
                                        setShowCustomInput(
                                            (prevState) => !prevState
                                        );
                                    }}
                                    className={`btn btn--full ${
                                        showCustomInput
                                            ? 'btn--blue'
                                            : 'btn--yellow'
                                    }`}
                                >
                                    Custom
                                </button>
                            </div>
                        </div>
                        <div className="setup-wizard--body--input input--small">
                            {showCustomInput && (
                                <input
                                    autoFocus
                                    type="number"
                                    placeholder="Enter value"
                                    value={formData.sampleSize}
                                    onChange={(e) =>
                                        updateFormData(
                                            SETUP_WIZARD_FORM_DATA_KEYS.SAMPLE_SIZE,
                                            Number(e.target.value)
                                        )
                                    }
                                />
                            )}
                        </div>
                        {/* <div className="setup-wizard--body--price">
                            Additional cost:{' '}
                            <span className="setup-wizard--body--price--marker">
                                $
                                {(formData.costPerPanelist * formData).toFixed(
                                    2
                                )}
                            </span>
                        </div> */}
                    </>
                );
            case SETUP_WIZARD_FORM_DATA_KEYS.VIDEO:
                return (
                    <>
                        <MediaManager
                            showMediaManager={isMediaLibraryOpen}
                            toggleMediaManager={setIsMediaLibraryOpen}
                            showMediaLibrary
                            applyCurrentMedia={(media) =>
                                selectMedia(
                                    { src: media.mediaURL },
                                    SETUP_WIZARD_FORM_DATA_KEYS.VIDEO
                                )
                            }
                            type="video"
                        >
                            {formData.video ? (
                                <div className="setup-wizard--body--file --uploaded">
                                    <video autoPlay={false} controls={true}>
                                        <source
                                            src={formData.video}
                                            type="video/mp4"
                                        />
                                        Your browser does not support the video
                                        tag.
                                    </video>
                                    <span
                                        className="setup-wizard--body--file--close"
                                        onClick={() =>
                                            updateFormData(
                                                SETUP_WIZARD_FORM_DATA_KEYS.VIDEO,
                                                null
                                            )
                                        }
                                    >
                                        <UilTimes />
                                    </span>
                                </div>
                            ) : (
                                <div
                                    className="setup-wizard--body--file"
                                    onClick={toggleMediaLibrary}
                                >
                                    <UilImagePlus size={60} />
                                    {/* <span>
                                    Drag and drop your video or upload from a{' '}
                                    <span>file</span>
                                </span> */}
                                    <span className="setup-wizard--body--file--text">
                                        Upload video from a file
                                    </span>
                                </div>
                            )}
                        </MediaManager>
                        <div className="setup-wizard--body--file--placeholder">
                            <span
                                className={classnames('setup-wizard--link', {
                                    disabled: !!formData.video
                                })}
                                onClick={() =>
                                    updateFormData(
                                        SETUP_WIZARD_FORM_DATA_KEYS.VIDEO,
                                        VIDEO_TEMPLATE.placeholder
                                    )
                                }
                            >
                                Or use our placeholder video
                            </span>
                        </div>
                        <div className="setup-wizard--important">
                            <p className="setup-wizard--important--content">
                                Recommended Videos: Up to 1min and under 200MB
                            </p>
                        </div>
                    </>
                );
            case SETUP_WIZARD_FORM_DATA_KEYS.COPY_TEXT:
                const currentView = '--desktop';

                return (
                    <>
                        <div className="text-highlighter--container">
                            <div className="text-highlighter--title">
                                Below is how your copy text will be displayed
                            </div>
                            <div
                                className={`text-highlighter--body ${
                                    formData.copyText.split(' ').length >
                                    WORD_NUM_BREAKPOINTS[1]
                                        ? '--sm'
                                        : formData.copyText.length >
                                          WORD_NUM_BREAKPOINTS[0]
                                        ? '--md'
                                        : '--lg'
                                }`}
                            >
                                <textarea
                                    autoFocus
                                    className={`text-highlighter--textarea ${currentView}`}
                                    value={formData.copyText}
                                    placeholder="Your text here..."
                                    onChange={(e) =>
                                        updateFormData(
                                            SETUP_WIZARD_FORM_DATA_KEYS.COPY_TEXT,
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                        <div className="setup-wizard--body--file--placeholder">
                            <span
                                className="setup-wizard--link"
                                onClick={() =>
                                    updateFormData(
                                        SETUP_WIZARD_FORM_DATA_KEYS.COPY_TEXT,
                                        COPY_TEMPLATE.placeholder
                                    )
                                }
                            >
                                Or use our placeholder text
                            </span>
                        </div>
                    </>
                );
            case SETUP_WIZARD_FORM_DATA_KEYS.HOTSPOT:
                const handleOptionFields = (
                    keyName,
                    data,
                    deletedOptionIDs = []
                ) => {
                    setFormData((prevState) => ({
                        ...prevState,
                        hotspotOptions: [...data]
                    }));
                };

                const addNewOption = () => {
                    const newObjectBox: OptionData = {
                        new: true,
                        label: '',
                        value: String(formData.hotspotOptions.length + 1),
                        coordinates: {
                            rotation: 0,
                            x: 100,
                            y: 100,
                            height: 200,
                            width: 200
                        }
                    };
                    setFormData((prevState) => ({
                        ...prevState,
                        hotspotOptions: [
                            ...formData.hotspotOptions,
                            newObjectBox
                        ]
                    }));
                };

                const clearOptions = () => {
                    setFormData((prevState) => ({
                        ...prevState,
                        hotspotOptions: []
                    }));
                };

                const clearComputerVision = () => {
                    setFormData((prevState) => ({
                        ...prevState,
                        hotspot: null,
                        hotspotOptions: []
                    }));
                };

                // only show placeholder button if not already selected
                const showPlaceholderButton = !(
                    formData.hotspot == IMAGE_TEMPLATE.placeholder
                );

                return (
                    <>
                        <ComputerVision
                            imageURL={formData.hotspot}
                            question={{
                                id: 1000,
                                title: 'tempQuestion',
                                questionTexts: [],
                                type: QTYPES.HOTSPOT,
                                options: formData.hotspotOptions
                            }}
                            handleOptionFields={handleOptionFields}
                            addNewOption={addNewOption}
                            clearOptions={clearOptions}
                            setValue={() => {}}
                            clearComputerVision={clearComputerVision}
                            enableComputerVision={enableComputerVision}
                            toggleEnableComputerVision={() => {
                                setEnableComputerVision(
                                    (prevState) => !prevState
                                );
                            }}
                            updateOptions={(optionedCV) => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    hotspotOptions: optionedCV
                                }));
                            }}
                            isLoading={isLoading}
                            updateIsLoading={(value) => {
                                setIsLoading(value);
                            }}
                            selectMedia={(media) =>
                                selectMedia(
                                    media,
                                    SETUP_WIZARD_FORM_DATA_KEYS.HOTSPOT
                                )
                            }
                            toastNotification={props.toastNotification}
                        />
                        {showPlaceholderButton && (
                            <div
                                className="setup-wizard--body--file--placeholder"
                                style={{ marginTop: 0 }}
                            >
                                <span
                                    className="setup-wizard--link"
                                    onClick={() => {
                                        clearComputerVision();
                                        updateFormData(
                                            SETUP_WIZARD_FORM_DATA_KEYS.HOTSPOT,
                                            IMAGE_TEMPLATE.placeholder
                                        );
                                    }}
                                >
                                    Or use our placeholder image
                                </span>
                            </div>
                        )}
                        {isMediaLibraryOpen && (
                            <MediaLibraryAddModal
                                isOpen={isMediaLibraryOpen}
                                setSelectedMedia={(media) => {
                                    selectMedia(
                                        media,
                                        SETUP_WIZARD_FORM_DATA_KEYS.HOTSPOT
                                    );
                                }}
                                notify={props.notify}
                                onCancel={toggleMediaLibrary}
                                onClose={toggleMediaLibrary}
                                type={'image'}
                                showMediaLibrary={true}
                            />
                        )}
                    </>
                );
            case SETUP_WIZARD_FORM_DATA_KEYS.SUPPORTED_DEVICES:
                const screens = devices;
                const getIcon = (screen: string) => {
                    switch (screen) {
                        case deviceMapping.mobile.label:
                            return (
                                <UilMobileAndroid
                                    size={40}
                                    style={{ marginTop: '20px' }}
                                />
                            );
                        case deviceMapping.tablet.label:
                            return (
                                <UilTablet
                                    size={50}
                                    style={{ marginTop: '10px' }}
                                />
                            );
                        default:
                            return <UilDesktop size={60} />;
                    }
                };

                const screenButtons = screens.map((screen, i) => (
                    <div key={i} className="setup-wizard--body--screens--item">
                        <button
                            className={`btn btn--full ${
                                formData.supportedDevices.includes(screen.label)
                                    ? 'btn--blue'
                                    : 'btn--lt-gray'
                            }`}
                            onClick={() => {
                                const foundScreenIndex =
                                    formData.supportedDevices.findIndex(
                                        (val) => val === screen.label
                                    );
                                const newScreens = [
                                    ...formData.supportedDevices
                                ];

                                if (foundScreenIndex > -1) {
                                    newScreens.splice(foundScreenIndex, 1);
                                } else {
                                    newScreens.push(screen.label);
                                }
                                updateFormData(
                                    SETUP_WIZARD_FORM_DATA_KEYS.SUPPORTED_DEVICES,
                                    newScreens
                                );
                            }}
                        >
                            {getIcon(screen.label)}
                            {screen.label}
                            {formData.supportedDevices.includes(
                                screen.label
                            ) && (
                                <div className="setup-wizard--body--screens--item--check">
                                    <UilCheck size={20} />
                                </div>
                            )}
                        </button>
                        <span>
                            {VIDEO_TEMPLATE.recommendedScreens.includes(screen)
                                ? 'Recommended'
                                : 'Not Recommended'}
                        </span>
                    </div>
                ));

                return (
                    <div className="setup-wizard--body--screens">
                        {screenButtons}
                    </div>
                );
            case SETUP_WIZARD_FORM_DATA_KEYS.CHECKOUT:
                const checkoutObj = {
                    projectFee: 1000,
                    // panelistFee: 0,
                    // subtotal: 1000,
                    tax: 130,
                    total: 1130
                };
                const checkoutKeys = Object.keys(checkoutObj);
                const specialKeys = ['subtotal', 'total'];

                const unCamelCase = (str) => {
                    return (
                        str
                            // insert a space between lower & upper
                            .replace(/([a-z])([A-Z])/g, '$1 $2')
                            // space before last upper in a sequence followed by lower
                            .replace(/\b([A-Z]+)([A-Z])([a-z])/, '$1 $2$3')
                            // uppercase the first character
                            .replace(/^./, function (str) {
                                return str.toUpperCase();
                            })
                    );
                };

                return (
                    <>
                        <div className="setup-wizard--body--checkout">
                            {checkoutKeys.map((key, i) => (
                                <React.Fragment key={i}>
                                    {specialKeys.includes(key) && (
                                        <div className="setup-wizard--body--checkout--item">
                                            <span></span>
                                            <span className="setup-wizard--body--checkout--item--underline">{` `}</span>
                                        </div>
                                    )}
                                    <div className="setup-wizard--body--checkout--item">
                                        <span>{unCamelCase(key)}</span>
                                        <span>
                                            $ {checkoutObj[key].toFixed(2)}
                                        </span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                        <div className="setup-wizard--important">
                            <span className="setup-wizard--important--header">
                                Note
                            </span>
                            <span className="setup-wizard--important--content">
                                Contact us for the most up-to-date pricing on
                                panelist recruitment fees. Prices subject to
                                change.
                            </span>
                        </div>
                    </>
                );
            default:
                break;
        }
    };

    const getBackgroundImage = (type: string) => {
        switch (type) {
            case SETUP_WIZARD_FORM_DATA_KEYS.NAME:
                return 'start.gif';
            case SETUP_WIZARD_FORM_DATA_KEYS.COUNTRY:
                return 'lookout.gif';
            case SETUP_WIZARD_FORM_DATA_KEYS.SAMPLE_SIZE:
                return 'ufo.gif';
            case SETUP_WIZARD_FORM_DATA_KEYS.VIDEO:
                return 'transfer.gif';
            case SETUP_WIZARD_FORM_DATA_KEYS.SUPPORTED_DEVICES:
                return 'navigate.gif';
            case SETUP_WIZARD_FORM_DATA_KEYS.CHECKOUT:
                return 'launch.gif';
            default:
                return 'start.gif';
        }
    };

    if (isRequesting) {
        return <Spinner />;
    }

    return (
        <div className="setup-wizard--container">
            <div className="setup-wizard--question">
                <span className="setup-wizard--question--subtitle">
                    Step {step + 1} of {template.questions.length}
                </span>
                <span className="setup-wizard--question--content">
                    {template.questions[step].questionWording}
                </span>

                {template.questions[step].questionSubtext.map((text, i) => (
                    <span key={i} className="setup-wizard--question--subtext">
                        {text}
                    </span>
                ))}
            </div>
            <div className="setup-wizard--body">
                {getInput(template.questions[step].type)}
            </div>
            {template.questions[step].importantWording.length > 0 && (
                <div className="setup-wizard--important">
                    <span className="setup-wizard--important--content">
                        {template.questions[step].importantWording.map(
                            (words, i) => (
                                <p key={i}>{words}</p>
                            )
                        )}
                    </span>
                </div>
            )}
            {questionError && (
                <div className="setup-wizard--error">
                    <span>{questionError}</span>
                </div>
            )}
            {/* <div className="setup-wizard--dots">
                {Array.from(Array(template.questions.length)).map((dot, i) => (
                    <div
                        key={i}
                        className={`setup-wizard--dots--dot ${
                            step === i ? 'active' : null
                        }`}
                        onClick={() => {
                            if (step !== i) {
                                handleStep(step, i);
                            }
                        }}
                    ></div>
                ))}
            </div> */}
            <div className="setup-wizard--actions">
                <button
                    className="btn btn--md btn--lt-gray"
                    onClick={() => {
                        if (step >= 1) {
                            handleStep(step, step - 1);
                        } else {
                            props.history.push(PROJECT_SELECTION);
                        }
                    }}
                >
                    Back
                </button>

                <button
                    className="btn btn--md btn--green"
                    onClick={() => {
                        if (step < template.questions.length - 1) {
                            handleStep(step, step + 1);
                        } else {
                            handleLaunch();
                        }
                    }}
                >
                    {step < template.questions.length - 1
                        ? 'Next'
                        : 'Launch Project'}
                </button>
            </div>
        </div>
    );
};

export default NotifyHOC(withRouter(SetupWizard));
